import _ from 'lodash';
import BaseCtrl from 'edge/platform/base/BaseCtrl';
import MockUpUtil from 'gw-portals-util-js/MockUpUtil';
import MetadataDrivenUtil from 'gw-portals-driven-ui-js/MetadataDrivenUtil';

const mockUpUtil = new MockUpUtil();
const fn = ($filter, $scope, $translate, ModalService, StateUtils, submissionViewModel) => {
    const ctrl = Object.create(BaseCtrl);
    let submissionVm = submissionViewModel();
    const paModel = submissionVm.lobData.personalAuto.value;
    const accountHolder = submissionVm.value.getAccountHolderPerson();

    ctrl.defineWatches = () => {
        $scope.$watch(() => submissionViewModel().value, () => {
            submissionVm = submissionViewModel();
            $scope.vehicles = submissionVm.lobData.personalAuto.coverables.vehicles;
        }, true);
    };

    ctrl.init = () => {
        $scope.isMetadataDrivenUIEnabled = MetadataDrivenUtil.isStepEnabled('pa', 'pa-vehicle-details');
        $scope.page = {
            readonly: false
        };
        let selectedVehicle;

        $scope.vehicles = submissionVm.lobData.personalAuto.coverables.vehicles;

        /**
         *  If there are no vehicles in the table, initialise an empty vehicle to be populated,
         *  otherwise on first enter, when there are vehicles, pick the first one from the array.
         */
        if (paModel.coverables.vehicles.length === 0) {
            selectedVehicle = paModel.coverables.createVehicle(accountHolder);
        } else {
            selectedVehicle = paModel.coverables.vehicles[0];
        }

        // This function is used to remove prefill data needed to obtain a quote for streamlined submissions.
        const sanitisePrefillDataFromQuickQuote = (vehicles) => {
            const licenceMockData = mockUpUtil.getMockData('lobData.personalAuto.coverables.drivers.driver.license');
            const vehicleVinMockData = mockUpUtil.getMockData('lobData.personalAuto.coverables.vehicles.vehicle.vin');

            const vehicle = _.find(vehicles, {
                license: licenceMockData
            });
            if (angular.isDefined(vehicle)) {
                vehicle.license = undefined;
                vehicle.costNew.amount = undefined;
                if (vehicle.vin === vehicleVinMockData) {
                    vehicle.vin = undefined;
                }
            }
        };
        sanitisePrefillDataFromQuickQuote(paModel.coverables.vehicles);

        $scope.page.selectedVehicle = submissionVm.lobData.personalAuto.coverables.vehicles.findFirstElement(selectedVehicle);
    };

    ctrl.addVehicle = (form) => {
        if (ctrl._isValid(form)) {
            const newVehicle = paModel.coverables.createVehicle();
            $scope.page.selectedVehicle = submissionVm.lobData.personalAuto.coverables.vehicles.findFirstElement(newVehicle);
            $scope.page.readonly = false;
            $scope.$broadcast('update-form');
            form.submitted = false;
            form.setPristine();
            form.$setUntouched();
        }
    };

    ctrl.removeVehicle = (vehicle) => {
        const confirmationMsg = {
            key: 'quoteandbind.pa.controllers.PAVehicleDetailsCtrl.RemoveConfirmationMsg',
            args: {
                vehicle: vehicle.getDisplayName(undefined, $filter)
            }
        };
        ModalService.showWarning(
            'quoteandbind.pa.controllers.PAVehicleDetailsCtrl.Remove Vehicle?', confirmationMsg, '',
            $filter('translate')('quoteandbind.pa.controllers.PAVehicleDetailsCtrl.Yes'),
            'gw-btn-primary',
            $filter('translate')('quoteandbind.pa.controllers.PAVehicleDetailsCtrl.No')
        )
            .result.then(() => {
                let vehicleObj;
                if (submissionVm.lobData.personalAuto.coverables.vehicles.findFirstElement(vehicle) === $scope.page.selectedVehicle) { // If we're about to delete the current vehicle, show the first vehicle or blank
                    if (paModel.coverables.vehicles.length > 1) {
                        if (paModel.coverables.vehicles[0] !== vehicle) {
                            vehicleObj = paModel.coverables.vehicles[0];
                        } else {
                            vehicleObj = paModel.coverables.vehicles[1];
                        }
                    }
                } else {
                    vehicleObj = $scope.page.selectedVehicle.value;
                }
                paModel.coverables.removeVehicle(vehicle);
                $scope.page.selectedVehicle = submissionVm.lobData.personalAuto.coverables.vehicles.findFirstElement(vehicleObj);
                $scope.$broadcast('update-form');
            });
    };

    ctrl.isVehicleValid = () => {
        return _.every($scope.vehicles.children, (vehicle) => {
            return vehicle.aspects.subtreeValid;
        });
    };

    ctrl.editVehicle = (vehicle, form) => { // Validate the current input, and if correct, save the current changes and select the next vehicle
        if (ctrl._isValid(form)) {
            $scope.page.selectedVehicle = submissionVm.lobData.personalAuto.coverables.vehicles.findFirstElement(vehicle);
        } else {
            form.submitted = true;
        }
        $scope.page.readonly = false;
    };

    ctrl._isVehicleEmpty = ({
        vin, model, make, year, licenseState, costNew
    }) => {
        const nonLicenseFieldsEmpty = [vin, model, make, year, costNew.amount].every(prop => {
            return prop === null || prop === undefined || prop === '';
        });
        const licenseStateEmpty = licenseState === null || licenseState === undefined || licenseState === accountHolder.primaryAddress.state;
        return nonLicenseFieldsEmpty && licenseStateEmpty;
    };

    ctrl._isValid = () => {
        if (!ctrl.isVehicleValid()) {
            return false;
        }
        if (!$scope.isMetadataDrivenUIEnabled) {
            const licenceMatchedVehicle = ctrl._ensureUniqueLicensePlate($scope.page.selectedVehicle.value);
            const vinMatchedVehicle = ctrl._ensureUniqueVin($scope.page.selectedVehicle.value);
            if (licenceMatchedVehicle) {
                const licenceMatchedMsg = $translate('quoteandbind.pa.controllers.PAVehicleDetailsCtrl.LicenceMatchMsg', {
                    licenceMatchedVehicle: licenceMatchedVehicle.getDisplayName(undefined, $filter)
                });
                ModalService.showError('quoteandbind.pa.controllers.PAVehicleDetailsCtrl.Licence plate is not unique', licenceMatchedMsg);
                return false;
            } else if (vinMatchedVehicle) {
                const vinMatchedMsg = $translate('quoteandbind.pa.controllers.PAVehicleDetailsCtrl.VinMatchMsg', {
                    vinMatchedVehicle: vinMatchedVehicle.getDisplayName(undefined, $filter)
                });
                ModalService.showError('quoteandbind.pa.controllers.PAVehicleDetailsCtrl.VIN is not unique', vinMatchedMsg);
                return false;
            }
        }
        return true;
    };

    ctrl._ensureUniqueLicensePlate = (vehicle) => {
        if (vehicle.license) {
            const vehicleMatch = _.filter(paModel.coverables.vehicles, {
                license: vehicle.license
            });
            if (!vehicleMatch) {
                return undefined;
            } else if (vehicleMatch.length === 1) {
                return vehicleMatch[0] !== vehicle ? vehicleMatch[0] : undefined;
            }
            return _.find(vehicleMatch, (v) => {
                return v !== vehicle;
            });
        }
    };

    ctrl._ensureUniqueVin = (vehicle) => {
        const vehicleMatch = _.filter(paModel.coverables.vehicles, {
            vin: vehicle.vin
        });
        if (!vehicleMatch) {
            return undefined;
        } else if (vehicleMatch.length === 1) {
            return vehicleMatch[0] !== vehicle ? vehicleMatch[0] : undefined;
        }
        return _.find(vehicleMatch, (v) => {
            return v !== vehicle;
        });
    };

    ctrl._setVehicleDefaults = () => {
        _.forEach(paModel.coverables.vehicles, (vehicle) => {
            vehicle.primaryUse = 'commuting';
        });
    };

    ctrl.next = function (callback) {
        $scope.form.submitted = true;
        if (!ctrl._isValid() || $scope.form.valid === false) {
            callback();
            return;
        }
        ctrl._setVehicleDefaults();
        callback(submissionVm);
    };
    ctrl.previous = function (callback) {
        // remove empty vehicle if no changes have been made
        if (paModel.coverables.vehicles.length === 1 && ctrl._isVehicleEmpty(paModel.coverables.vehicles[0])) {
            paModel.coverables.vehicles.pop();
        }
        callback(submissionVm);
    };
    ctrl.cancel = function (callback) {
        callback(submissionVm);
    };

    return ctrl.create($scope);
};
fn.$inject = ['$filter', '$scope', '$translate', 'ModalService', 'StateUtils', 'submissionViewModel'];

export default fn;
